/**
 * CBO Discovery Component
 * Sponsor dashboard to find and invite compatible CBOs to programs
 * Shows match scores, predicted outcomes, and component breakdowns
 * Trust Marketplace - Program-CBO Matching System
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Progress } from '../ui/progress';
import { getWeaveinApiUrl } from '@/src/config';
import {
  Building2,
  Users,
  TrendingUp,
  Award,
  DollarSign,
  MapPin,
  Target,
  Send,
  AlertCircle,
  CheckCircle2,
  Star,
  BarChart3,
  Network,
  Heart,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Search,
  Plus,
  X
} from 'lucide-react';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import ProviderRecruitment from './ProviderRecruitment';
import CBOProfileModal from './CBOProfileModal';
import { ParticipationTypeSelector } from '../sponsors/ParticipationTypeSelector';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';
interface MatchScore {
  id: number;
  program_id: number;
  cbo_id: number;
  sponsor_id: number;
  match_score: number | null;
  match_quality: string | null;
  service_alignment_score: number;
  semantic_alignment_score: number;
  geographic_fit_score: number;
  capacity_fit_score: number;
  performance_score: number;
  demographic_alignment_score: number;
  network_strength_score: number;
  ml_success_probability: number;
  ml_model_version: string;
  predicted_enrollments_year: number;
  predicted_completion_rate: number;
  predicted_cost_per_enrollment: number;
  estimated_roi: number;
  raw_distance_miles: number;
  raw_capacity_available: number;
  raw_success_rate: number;
  raw_quality_score: number;
  scoring_method: string;
  scoring_timestamp: string;
  score_valid_until: string;
  meets_minimum_requirements: boolean;
  eligibility_notes: string;
  program_name: string;
  program_type: string;
  cbo_name: string;
  // HYBRID provider scoring
  active_provider_score?: number;
  potential_provider_score?: number;
  active_provider_count?: number;
  potential_provider_count?: number;
}

interface CBODiscoveryProps {
  sponsorId: number;
  programId: number;
  onInviteSent?: () => void;
}

const CBODiscovery: React.FC<CBODiscoveryProps> = ({ sponsorId, programId, onInviteSent }) => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [cbos, setCbos] = useState<MatchScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [minMatchScore, setMinMatchScore] = useState<number>(70);
  const [matchQualityFilter, setMatchQualityFilter] = useState<string>('');

  // Invitation modal
  const [selectedCBO, setSelectedCBO] = useState<MatchScore | null>(null);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [invitationMessage, setInvitationMessage] = useState('');
  const [inviting, setInviting] = useState(false);

  // Expanded details
  const [expandedCBO, setExpandedCBO] = useState<number | null>(null);

  // CBO Profile Modal
  const [profileCBOId, setProfileCBOId] = useState<number | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Participation type selection
  const [participationSelection, setParticipationSelection] = useState<{
    participationType: string;
    milestones?: any[];
    totalFunding: number;
    paymentTerms: string;
    startDate?: string;
    endDate?: string;
  }>({
    participationType: 'PROGRAM',
    totalFunding: 0,
    paymentTerms: 'Monthly'
  });

  // Organization Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [showNewOrgForm, setShowNewOrgForm] = useState(false);
  const [newOrgData, setNewOrgData] = useState({
    name: '',
    city: '',
    state: '',
    county: '',
    zip_code: '',
    phone: '',
    email: '',
    contact_name: '',
    notes: ''
  });
  const [creatingOrg, setCreatingOrg] = useState(false);

  useEffect(() => {
    fetchRecommendedCBOs();
  }, [sponsorId, programId, minMatchScore, matchQualityFilter]);

  const fetchRecommendedCBOs = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');

      // First try auto-match from Provider Directory (28,000+ providers)
      const autoMatchParams = new URLSearchParams({
        min_match_score: minMatchScore.toString(),
        limit: '50',
        radius_miles: '25'
      });

      const autoMatchUrl = getWeaveinApiUrl(`/api/sponsors/${sponsorId}/programs/${programId}/auto-match-providers?${autoMatchParams}`);

      let cbosData: any[] = [];

      try {
        const autoMatchResponse = await authFetch(autoMatchUrl);

        if (autoMatchResponse.ok) {
          const autoMatchData = await autoMatchResponse.json();
          if (autoMatchData.providers && autoMatchData.providers.length > 0) {
            cbosData = autoMatchData.providers;
            console.log(`Auto-matched ${cbosData.length} providers from Provider Directory`);
          }
        }
      } catch (autoMatchErr) {
        console.warn('Auto-match failed, falling back to pre-calculated scores:', autoMatchErr);
      }

      // If auto-match didn't return results, fall back to pre-calculated scores
      if (cbosData.length === 0) {
        const params = new URLSearchParams({
          min_match_score: minMatchScore.toString(),
          ...(matchQualityFilter && { match_quality: matchQualityFilter })
        });

        const url = getWeaveinApiUrl(`/api/sponsors/${sponsorId}/programs/${programId}/recommended-cbos?${params}`);

        const response = await authFetch(url);

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Program not found. Please create a program first or select an existing program.');
          }
          throw new Error(`Failed to fetch recommended CBOs: ${response.status}`);
        }

        const data = await response.json();
        cbosData = Array.isArray(data) ? data : [];
      }

      // Filter by match quality if specified
      if (matchQualityFilter && cbosData.length > 0) {
        cbosData = cbosData.filter(cbo => cbo.match_quality === matchQualityFilter);
      }

      // Filter out any null/undefined entries
      const validCbos = cbosData.filter(cbo => cbo && cbo.id);
      setCbos(validCbos);
    } catch (err) {
      console.error('Error fetching CBOs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch recommended CBOs');
    } finally {
      setLoading(false);
    }
  };

  // Search for organizations
  const searchOrganizations = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl(`/api/organizations/search?q=${encodeURIComponent(query)}&limit=20`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId || ''
          }
        }
      );

      if (response.ok) {
        const results = await response.json();
        setSearchResults(results);
      }
    } catch (err) {
      console.error('Error searching organizations:', err);
    } finally {
      setSearching(false);
    }
  };

  // Create new opportunity organization
  const createOpportunityOrg = async () => {
    if (!newOrgData.name) {
      alert('Organization name is required');
      return;
    }

    try {
      setCreatingOrg(true);
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');

      const response = await fetch(
        getWeaveinApiUrl('/api/organizations/opportunity'),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId || ''
          },
          body: JSON.stringify(newOrgData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create organization');
      }

      const result = await response.json();
      const newOrg = result.organization;

      // Open invitation modal for the new org
      setSelectedCBO({
        id: 0,
        program_id: programId,
        cbo_id: newOrg.id,
        sponsor_id: sponsorId,
        match_score: 0,
        match_quality: 'Manual',
        service_alignment_score: 0,
        semantic_alignment_score: 0,
        geographic_fit_score: 0,
        capacity_fit_score: 0,
        performance_score: 0,
        demographic_alignment_score: 0,
        network_strength_score: 0,
        ml_success_probability: 0,
        ml_model_version: '',
        predicted_enrollments_year: 0,
        predicted_completion_rate: 0,
        predicted_cost_per_enrollment: 0,
        estimated_roi: 0,
        raw_distance_miles: 0,
        raw_capacity_available: 0,
        raw_success_rate: 0,
        raw_quality_score: 0,
        scoring_method: 'manual',
        scoring_timestamp: '',
        score_valid_until: '',
        meets_minimum_requirements: true,
        eligibility_notes: '',
        program_name: '',
        program_type: '',
        cbo_name: newOrg.name
      });
      setShowInvitationModal(true);

      // Reset form
      setNewOrgData({
        name: '',
        city: '',
        state: '',
        county: '',
        zip_code: '',
        phone: '',
        email: '',
        contact_name: '',
        notes: ''
      });
      setShowNewOrgForm(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setCreatingOrg(false);
    }
  };

  // Invite organization from search results
  const inviteFromSearch = (org: any) => {
    setSelectedCBO({
      id: 0,
      program_id: programId,
      cbo_id: org.id,
      sponsor_id: sponsorId,
      match_score: null,
      match_quality: 'Manual',
      service_alignment_score: 0,
      semantic_alignment_score: 0,
      geographic_fit_score: 0,
      capacity_fit_score: 0,
      performance_score: 0,
      demographic_alignment_score: 0,
      network_strength_score: 0,
      ml_success_probability: 0,
      ml_model_version: '',
      predicted_enrollments_year: 0,
      predicted_completion_rate: 0,
      predicted_cost_per_enrollment: 0,
      estimated_roi: 0,
      raw_distance_miles: 0,
      raw_capacity_available: 0,
      raw_success_rate: 0,
      raw_quality_score: 0,
      scoring_method: 'manual',
      scoring_timestamp: '',
      score_valid_until: '',
      meets_minimum_requirements: true,
      eligibility_notes: '',
      program_name: '',
      program_type: '',
      cbo_name: org.name
    });
    setShowInvitationModal(true);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleInviteCBO = async () => {
    if (!selectedCBO) return;

    try {
      setInviting(true);
      const url = getWeaveinApiUrl(`/api/sponsors/${sponsorId}/programs/${programId}/invite-cbos`);
      const token = localStorage.getItem('auth_token');

      const response = await authFetch(url, {
        method: 'POST',
        jsonBody: {
          cbo_id: selectedCBO.cbo_id,
          program_id: programId,
          invitation_message: invitationMessage,
          expires_at: null // No expiration
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send invitation');
      }

      alert(`Invitation sent successfully to ${selectedCBO.cbo_name}!`);
      setShowInvitationModal(false);
      setSelectedCBO(null);
      setInvitationMessage('');
      fetchRecommendedCBOs(); // Refresh
      onInviteSent?.(); // Notify parent to refresh invitations list

    } catch (err) {
      console.error('Error sending invitation:', err);
      alert(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const getMatchQualityColor = (quality: string) => {
    switch (quality) {
      case 'EXCELLENT':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'GOOD':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'FAIR':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'POOR':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getMatchQualityIcon = (quality: string) => {
    switch (quality) {
      case 'EXCELLENT':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'GOOD':
        return <Star className="w-4 h-4" />;
      case 'FAIR':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const ScoreBreakdown: React.FC<{ cbo: MatchScore }> = ({ cbo }) => {
    const components = [
      { label: 'Service Alignment', score: cbo.service_alignment_score, icon: Heart },
      { label: 'Semantic Alignment', score: cbo.semantic_alignment_score, icon: BarChart3 },
      { label: 'Geographic Fit', score: cbo.geographic_fit_score, icon: MapPin },
      { label: 'Capacity Fit', score: cbo.capacity_fit_score, icon: Users },
      { label: 'Performance', score: cbo.performance_score, icon: TrendingUp },
      { label: 'Demographics', score: cbo.demographic_alignment_score, icon: Target },
      { label: 'Network Strength', score: cbo.network_strength_score, icon: Network }
    ];

    // Add provider scores if available (HYBRID scoring)
    if (cbo.active_provider_score !== undefined && cbo.active_provider_score !== null) {
      components.push({
        label: `Active Providers (${cbo.active_provider_count || 0})`,
        score: cbo.active_provider_score,
        icon: UserPlus
      });
    }
    if (cbo.potential_provider_score !== undefined && cbo.potential_provider_score !== null) {
      components.push({
        label: `Potential Providers (${cbo.potential_provider_count || 0})`,
        score: cbo.potential_provider_score,
        icon: UserPlus
      });
    }

    return (
      <div className="space-y-3">
        {components.map(({ label, score, icon: Icon }) => (
          <div key={label} className="space-y-1">
            <div className="flex justify-between text-sm">
              <div className="flex items-center gap-2 text-gray-700">
                <Icon className="w-4 h-4 text-gray-500" />
                <span>{label}</span>
              </div>
              <span className="font-medium">{score ? Number(score).toFixed(1) : 0}%</span>
            </div>
            <Progress value={score || 0} className="h-1.5" />
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <p>Error: {error}</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          {!tabbed && <h2 className="text-2xl font-bold text-gray-900">{t('marketplace.cbo_discovery_recruitment', 'CBO Discovery & Recruitment')}</h2>}
          <p className="text-gray-600 mt-1">{t('marketplace.find_invite_and_recruit_organizations', 'Find, invite, and recruit organizations and individuals for your program')}</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Building2 className="w-4 h-4" />
          <span>{cbos.length} CBOs matched</span>
        </div>
      </div>

      <Card className="p-6">
        <Tabs defaultValue="recommended" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="recommended">{t('marketplace.recommended_cbos', 'Recommended CBOs')}</TabsTrigger>
            <TabsTrigger value="search">{t('marketplace.search_add_organizations', 'Search & Add Organizations')}</TabsTrigger>
          </TabsList>

        {/* Search & Add Organizations Tab */}
        <TabsContent value="search" className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('marketplace.find_organizations', 'Find Organizations')}</h3>

            {/* Search Input */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t('marketplace.search_by_name_city_zip', 'Search by name, city, zip code...')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchOrganizations(e.target.value);
                }}
                className="pl-10"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <LoadingSpinner className="w-4 h-4" />
                </div>
              )}
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
                {searchResults.map((org) => (
                  <div key={org.id} className="p-3 hover:bg-gray-50 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{org.name}</div>
                      <div className="text-sm text-gray-500">
                        {org.address || org.city || 'No address'}
                        {org.customer_status && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            {org.customer_status}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button size="sm" onClick={() => inviteFromSearch(org)}>
                      <Send className="w-3 h-3 mr-1" />
                      {t('marketplace.invite', 'Invite')}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
              <p className="text-sm text-gray-500 text-center py-4">
                No organizations found matching "{searchQuery}"
              </p>
            )}

            {/* Create New Organization */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900">{t('marketplace.cant_find_the_organization', 'Can\'t find the organization?')}</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewOrgForm(!showNewOrgForm)}
                >
                  {showNewOrgForm ? (
                    <>
                      <X className="w-4 h-4 mr-1" />
                      {t('marketplace.cancel', 'Cancel')}
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-1" />
                      {t('marketplace.add_new_organization', 'Add New Organization')}
                    </>
                  )}
                </Button>
              </div>

              {showNewOrgForm && (
                <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('marketplace.organization_name', 'Organization Name *')}
                      </label>
                      <Input
                        value={newOrgData.name}
                        onChange={(e) => setNewOrgData({ ...newOrgData, name: e.target.value })}
                        placeholder={t('marketplace.organization_name_1', 'Organization name')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('marketplace.contact_name', 'Contact Name')}
                      </label>
                      <Input
                        value={newOrgData.contact_name}
                        onChange={(e) => setNewOrgData({ ...newOrgData, contact_name: e.target.value })}
                        placeholder={t('marketplace.primary_contact', 'Primary contact')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('marketplace.phone', 'Phone')}
                      </label>
                      <Input
                        value={newOrgData.phone}
                        onChange={(e) => setNewOrgData({ ...newOrgData, phone: e.target.value })}
                        placeholder="(555) 555-5555"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('marketplace.email', 'Email')}
                      </label>
                      <Input
                        value={newOrgData.email}
                        onChange={(e) => setNewOrgData({ ...newOrgData, email: e.target.value })}
                        placeholder="contact@organization.org"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('marketplace.city', 'City')}
                      </label>
                      <Input
                        value={newOrgData.city}
                        onChange={(e) => setNewOrgData({ ...newOrgData, city: e.target.value })}
                        placeholder={t('marketplace.city_1', 'City')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('marketplace.state', 'State')}
                      </label>
                      <Input
                        value={newOrgData.state}
                        onChange={(e) => setNewOrgData({ ...newOrgData, state: e.target.value })}
                        placeholder="CA"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('marketplace.county', 'County')}
                      </label>
                      <Input
                        value={newOrgData.county}
                        onChange={(e) => setNewOrgData({ ...newOrgData, county: e.target.value })}
                        placeholder={t('marketplace.county_1', 'County')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('marketplace.zip_code', 'Zip Code')}
                      </label>
                      <Input
                        value={newOrgData.zip_code}
                        onChange={(e) => setNewOrgData({ ...newOrgData, zip_code: e.target.value })}
                        placeholder="90210"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('marketplace.notes', 'Notes')}
                    </label>
                    <textarea
                      value={newOrgData.notes}
                      onChange={(e) => setNewOrgData({ ...newOrgData, notes: e.target.value })}
                      placeholder={t('marketplace.additional_notes_about_this_organization', 'Additional notes about this organization...')}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={createOpportunityOrg} disabled={creatingOrg || !newOrgData.name}>
                      {creatingOrg ? (
                        <>
                          <LoadingSpinner className="w-4 h-4 mr-2" />
                          {t('marketplace.creating', 'Creating...')}
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          {t('marketplace.create_invite', 'Create & Invite')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Recommended CBOs Tab */}
        <TabsContent value="recommended" className="space-y-4">
          {/* Filters */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketplace.minimum_match_score', 'Minimum Match Score')}</label>
                <select
                  value={minMatchScore}
                  onChange={(e) => setMinMatchScore(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="0">{t('marketplace.all_matches', 'All Matches')}</option>
                  <option value="90">{t('marketplace.excellent_90', 'Excellent (90+)')}</option>
                  <option value="75">{t('marketplace.good_75', 'Good (75+)')}</option>
                  <option value="60">{t('marketplace.fair_60', 'Fair (60+)')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketplace.match_quality', 'Match Quality')}</label>
                <select
                  value={matchQualityFilter}
                  onChange={(e) => setMatchQualityFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('marketplace.all_qualities', 'All Qualities')}</option>
                  <option value="EXCELLENT">{t('marketplace.excellent', 'Excellent')}</option>
                  <option value="GOOD">{t('marketplace.good', 'Good')}</option>
                  <option value="FAIR">{t('marketplace.fair', 'Fair')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* CBOs List */}
          <div className="space-y-4">
            {cbos.filter(cbo => cbo && cbo.id).map((cbo) => (
              <Card key={cbo.id} className="p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-1">{cbo.cbo_name}</h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{cbo.raw_distance_miles ? `${Number(cbo.raw_distance_miles).toFixed(1)} mi` : 'Same area'}</span>
                      </div>
                      {cbo.raw_capacity_available && (
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>{cbo.raw_capacity_available} capacity</span>
                        </div>
                      )}
                      {cbo.raw_success_rate && (
                        <div className="flex items-center gap-1">
                          <TrendingUp className="w-4 h-4" />
                          <span>{cbo.raw_success_rate}% success rate</span>
                        </div>
                      )}
                      {(cbo.active_provider_count !== undefined || cbo.potential_provider_count !== undefined) && (
                        <div className="flex items-center gap-1">
                          <Network className="w-4 h-4" />
                          <span>
                            {cbo.active_provider_count || 0} active
                            {cbo.potential_provider_count && cbo.potential_provider_count > 0 && (
                              <span className="text-orange-600 font-medium"> + {cbo.potential_provider_count} potential</span>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {cbo && cbo.match_score !== null && cbo.match_quality && (
                      <>
                        <div className={`px-3 py-1 rounded-full border flex items-center gap-1 ${getMatchQualityColor(cbo.match_quality)}`}>
                          {getMatchQualityIcon(cbo.match_quality)}
                          <span className="text-xs font-medium">{cbo.match_quality}</span>
                        </div>
                        <div className="text-3xl font-bold text-blue-600">{Number(cbo.match_score).toFixed(0)}%</div>
                      </>
                    )}
                  </div>
                </div>

                {/* Overall Match Bar */}
                {cbo && cbo.match_score !== null && (
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>{t('marketplace.overall_compatibility', 'Overall Compatibility')}</span>
                      <span>{Number(cbo.match_score).toFixed(1)}%</span>
                    </div>
                    <Progress value={cbo.match_score} className="h-3" />
                  </div>
                )}

                {/* Predicted Outcomes */}
                {cbo && (cbo.predicted_enrollments_year || cbo.estimated_roi) && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-blue-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">{cbo.predicted_enrollments_year || 'N/A'}</div>
                      <div className="text-xs text-gray-600">{t('marketplace.est_enrollmentsyear', 'Est. Enrollments/Year')}</div>
                    </div>
                    {cbo && cbo.predicted_completion_rate && (
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{Number(cbo.predicted_completion_rate).toFixed(0)}%</div>
                        <div className="text-xs text-gray-600">{t('marketplace.predicted_completion', 'Predicted Completion')}</div>
                      </div>
                    )}
                    {cbo && cbo.estimated_roi && (
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">{Number(cbo.estimated_roi).toFixed(1)}x</div>
                        <div className="text-xs text-gray-600">{t('marketplace.estimated_roi', 'Estimated ROI')}</div>
                      </div>
                    )}
                  </div>
                )}

                {/* Eligibility */}
                {cbo && !cbo.meets_minimum_requirements && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                    <div className="text-sm">
                      <div className="font-medium text-yellow-800">{t('marketplace.requirements_not_met', 'Requirements Not Met')}</div>
                      <div className="text-yellow-700">{cbo.eligibility_notes}</div>
                    </div>
                  </div>
                )}

                {/* Component Scores (Expandable) */}
                <div className="mb-4">
                  <button
                    onClick={() => setExpandedCBO(expandedCBO === cbo.id ? null : cbo.id)}
                    className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    {expandedCBO === cbo.id ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        {t('marketplace.hide_score_breakdown', 'Hide Score Breakdown')}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        {t('marketplace.view_score_breakdown', 'View Score Breakdown')}
                      </>
                    )}
                  </button>

                  {expandedCBO === cbo.id && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <ScoreBreakdown cbo={cbo} />
                    </div>
                  )}
                </div>

                {/* Provider Recruitment Opportunity */}
                {cbo && cbo.potential_provider_count && cbo.potential_provider_count > 0 && (
                  <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
                    <div className="flex items-start gap-2">
                      <UserPlus className="w-4 h-4 text-orange-600 mt-0.5" />
                      <div className="text-sm">
                        <div className="font-medium text-orange-800">{t('marketplace.provider_recruitment_opportunity', 'Provider Recruitment Opportunity')}</div>
                        <div className="text-orange-700">
                          This CBO has {cbo.potential_provider_count} provider{cbo.potential_provider_count > 1 ? 's' : ''} available to recruit.
                          Recruiting them could improve the match score by up to {Math.round(cbo.potential_provider_count * 0.5)}%.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Provider Details for auto-matched providers */}
                {cbo && cbo.provider_details && !cbo.cbo_id && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
                    <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {t('marketplace.provider_details', 'Provider Details')}
                    </h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {cbo.provider_details.address && (
                        <div>
                          <span className="text-gray-500">{t('marketplace.address', 'Address:')}</span>
                          <p className="text-gray-900">{cbo.provider_details.address}</p>
                          <p className="text-gray-900">{cbo.provider_details.city}, {cbo.provider_details.state} {cbo.provider_details.zip_code}</p>
                        </div>
                      )}
                      {cbo.provider_details.phone && (
                        <div>
                          <span className="text-gray-500">{t('marketplace.phone_1', 'Phone:')}</span>
                          <p className="text-gray-900">{cbo.provider_details.phone}</p>
                        </div>
                      )}
                      {cbo.provider_details.email && (
                        <div>
                          <span className="text-gray-500">{t('marketplace.email_1', 'Email:')}</span>
                          <p className="text-gray-900">{cbo.provider_details.email}</p>
                        </div>
                      )}
                      {cbo.provider_details.website && (
                        <div>
                          <span className="text-gray-500">{t('marketplace.website', 'Website:')}</span>
                          <a href={cbo.provider_details.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            {cbo.provider_details.website}
                          </a>
                        </div>
                      )}
                      {cbo.provider_details.organization_type && (
                        <div>
                          <span className="text-gray-500">{t('marketplace.type', 'Type:')}</span>
                          <p className="text-gray-900">{cbo.provider_details.organization_type}</p>
                        </div>
                      )}
                      {cbo.provider_details.services && cbo.provider_details.services.length > 0 && (
                        <div className="col-span-2">
                          <span className="text-gray-500">{t('marketplace.services', 'Services:')}</span>
                          <p className="text-gray-900">{cbo.provider_details.services.join(', ')}</p>
                        </div>
                      )}
                      <div className="col-span-2 flex gap-4">
                        {cbo.provider_details.accepts_medicaid && (
                          <Badge className="bg-green-100 text-green-800">{t('marketplace.accepts_medicaid', 'Accepts Medicaid')}</Badge>
                        )}
                        {cbo.provider_details.accepts_medicare && (
                          <Badge className="bg-blue-100 text-blue-800">{t('marketplace.accepts_medicare', 'Accepts Medicare')}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  {cbo && cbo.cbo_id && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setProfileCBOId(cbo.cbo_id);
                        setShowProfileModal(true);
                      }}
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      {t('marketplace.view_profile', 'View Profile')}
                    </Button>
                  )}

                  {/* Provider Recruitment */}
                  {cbo && cbo.potential_provider_count && cbo.potential_provider_count > 0 && (
                    <ProviderRecruitment
                      cboId={cbo.cbo_id}
                      cboName={cbo.cbo_name}
                      sponsorId={sponsorId}
                      activeProviderCount={cbo.active_provider_count || 0}
                      potentialProviderCount={cbo.potential_provider_count || 0}
                      onRecruitmentComplete={() => fetchRecommendedCBOs()}
                    />
                  )}

                  <Button
                    onClick={() => {
                      setSelectedCBO(cbo);
                      setShowInvitationModal(true);
                    }}
                    disabled={!cbo || !cbo.meets_minimum_requirements}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {t('marketplace.invite_to_program', 'Invite to Program')}
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {cbos.length === 0 && (
            <Card className="p-12 text-center">
              <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('marketplace.no_matching_cbos_found', 'No Matching CBOs Found')}</h3>
              <p className="text-gray-600">
                {t('marketplace.try_adjusting_your_filters_to', 'Try adjusting your filters to see more CBOs, or run the matching calculation to find compatible organizations.')}
              </p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </Card>

    {/* Invitation Modal */}
    <Dialog open={showInvitationModal} onOpenChange={setShowInvitationModal}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Invite {selectedCBO?.cbo_name} to Program</DialogTitle>
          <DialogDescription>
            {t('marketplace.send_an_invitation_to_this', 'Send an invitation to this CBO to join your program. They\'ll receive your message and can accept or decline.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Match Score Display */}
          {selectedCBO && selectedCBO.match_score !== null && (
            <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{t('marketplace.match_score', 'Match Score')}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-blue-600">{Number(selectedCBO.match_score).toFixed(0)}%</span>
                    {selectedCBO.match_quality && (
                      <Badge className={getMatchQualityColor(selectedCBO.match_quality)}>
                        {selectedCBO.match_quality}
                      </Badge>
                    )}
                  </div>
                </div>
                <Progress value={selectedCBO.match_score} className="h-2" />

                {/* Predicted Outcomes */}
                {selectedCBO.predicted_enrollments_year && (
                  <div className="mt-3 grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-blue-600">{selectedCBO.predicted_enrollments_year}</div>
                      <div className="text-xs text-gray-600">{t('marketplace.est_enrollmentsyear_1', 'Est. Enrollments/Year')}</div>
                    </div>
                    {selectedCBO.estimated_roi && (
                      <div>
                        <div className="text-lg font-bold text-purple-600">{Number(selectedCBO.estimated_roi).toFixed(1)}x</div>
                        <div className="text-xs text-gray-600">{t('marketplace.est_roi', 'Est. ROI')}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Participation Type Selection */}
            <ParticipationTypeSelector
              programId={programId}
              onSelectionChange={setParticipationSelection}
            />

            {/* Invitation Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('marketplace.invitation_message', 'Invitation Message')}
              </label>
              <textarea
                value={invitationMessage}
                onChange={(e) => setInvitationMessage(e.target.value)}
                placeholder={t('marketplace.invite_the_cbo_to_join', 'Invite the CBO to join your program and explain the benefits...')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowInvitationModal(false);
                  setSelectedCBO(null);
                  setInvitationMessage('');
                }}
                disabled={inviting}
              >
                {t('marketplace.cancel_1', 'Cancel')}
              </Button>
              <Button
                onClick={handleInviteCBO}
                disabled={inviting}
              >
                {inviting ? (
                  <>
                    <LoadingSpinner className="w-4 h-4 mr-2" />
                    {t('marketplace.sending', 'Sending...')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('marketplace.send_invitation', 'Send Invitation')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* CBO Profile Modal */}
      {profileCBOId && (
        <CBOProfileModal
          cboId={profileCBOId}
          isOpen={showProfileModal}
          onClose={() => {
            setShowProfileModal(false);
            setProfileCBOId(null);
          }}
          onInvite={() => {
            setShowProfileModal(false);
            setSelectedCBO(cbos.find(c => c.cbo_id === profileCBOId) || null);
            setShowInvitationModal(true);
          }}
        />
      )}
    </div>
  );
};

export default CBODiscovery;
